/**
 * BPReverifyAllPanel — full-scope BP image re-verify + remediate panel.
 *
 * Mounted at the TOP of Content Auditor → BP Audit view. Lets the Super Admin
 * kick off a background job that re-checks EVERY committed BP image (all 110
 * scenes, not just K) via strict MOSAIC engine-2.0-flash vision + regenerates any
 * flagged ones via MOSAIC image engine with a 4x-repeated anti-text prompt. Shows
 * live progress while running, final summary with stubborn-scene list when
 * done, and a "Regenerate 112 BLMs" trigger so the booklets pick up any new
 * images immediately.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Play, Loader2, ShieldCheck, AlertTriangle, CheckCircle2, RefreshCw,
  FileCheck2, Zap, Upload, X,
} from 'lucide-react';
import { ProductionImpactDisclosure } from './ProductionImpactDisclosure';
import { playSuccessChime, playFailureChime } from '../utils/maintenanceAudio';
import { ensureNotificationPermission, notifyOnCompletion } from '../utils/jobCompletionNotifier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const BPReverifyAllPanel = () => {
  const [status, setStatus] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [grades, setGrades] = useState(
    ['K', '1', '2', '3-4', '5-6', '7-8', '9-12']
  );
  const [attemptsCap, setAttemptsCap] = useState(3);
  const [forceGptImage1, setForceGptImage1] = useState(false);
  // v6.32.127 — Recent Runs history (last 5 runs from bp_reverify_runs Mongo collection)
  const [recentRuns, setRecentRuns] = useState([]);
  // v6.32.131 — Manual Review Queue: scenes the operator has marked as
  // "structurally impossible for current models" (e.g. Chinatown signage,
  // book pages). These are SKIPPED by both full sweeps and retry-one until
  // un-flagged. Backed by the bp_manual_review_pending Mongo collection.
  const [manualReview, setManualReview] = useState([]);
  const [manualReviewBusy, setManualReviewBusy] = useState(false);
  // v6.32.132 — Manual upload dialog state
  const [uploadTarget, setUploadTarget] = useState(null);  // {grade, context} of the scene being uploaded
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);
  const [uploadPassword, setUploadPassword] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/bp-auditor/reverify-all/status`, authCfg(),
      );
      setStatus(data);
    } catch (e) {
      // silent — panel just won't update
    }
  }, []);

  // v6.32.127 — Pulls recent runs from /diagnostics. /diagnostics is more
  // expensive than /status (does live env + Mongo + filesystem checks) so we
  // refresh it on a slower cadence than /status.
  const fetchRecentRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/bp-auditor/reverify-all/diagnostics`, authCfg(),
      );
      setRecentRuns(Array.isArray(data?.recent_mongo_runs) ? data.recent_mongo_runs : []);
    } catch (e) {
      // silent — history just won't update
    }
  }, []);

  // v6.32.131 — Pulls the manual-review queue.
  const fetchManualReview = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/bp-auditor/manual-review`, authCfg(),
      );
      setManualReview(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      // silent
    }
  }, []);

  // v6.32.131 — Flag a scene as manual-review-pending.
  const flagManualReview = async (s) => {
    if (manualReviewBusy) return;
    setManualReviewBusy(true);
    try {
      // eslint-disable-next-line no-alert
      const reason = window.prompt(
        `Mark this scene as 'Manual Review Pending'?\n\n` +
        `Gr ${s.grade} · ${s.context}\n\n` +
        `It will be SKIPPED by future full sweeps and retry-one calls until you un-flag it. ` +
        `Optional: why is this scene structurally impossible for current image-gen models?`,
        'Signage / open book pages / map text required for cultural authenticity',
      );
      if (reason === null) return;  // cancelled
      const { data } = await axios.post(
        `${API}/bp-auditor/manual-review`,
        { grade: s.grade, context: s.context, reason: (reason || '').slice(0, 500) },
        authCfg(),
      );
      if (data.ok) {
        toast.success(`Flagged Gr ${s.grade} · ${s.context} as Manual Review Pending.`);
        fetchManualReview();
      } else {
        toast.error(`Flag failed: ${data.error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(`Flag failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setManualReviewBusy(false);
    }
  };

  // v6.32.131 — Un-flag a scene (re-enables retries + full sweeps).
  const unflagManualReview = async (item) => {
    if (manualReviewBusy) return;
    setManualReviewBusy(true);
    try {
      const { data } = await axios.delete(
        `${API}/bp-auditor/manual-review`,
        { ...authCfg(), data: { grade: item.grade, context: item.context } },
      );
      if (data.ok) {
        toast.success(`Un-flagged Gr ${item.grade} · ${item.context}. Eligible for retry / re-verify again.`);
        fetchManualReview();
      } else {
        toast.error(`Un-flag failed: ${data.error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(`Un-flag failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setManualReviewBusy(false);
    }
  };

  // v6.32.132 — Manual image upload handlers.
  const openUploadDialog = (item) => {
    setUploadTarget({ grade: item.grade, context: item.context });
    setUploadFile(null);
    setUploadPreviewUrl(null);
    setUploadPassword('');
    setUploadNote('');
  };

  const closeUploadDialog = () => {
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    setUploadTarget(null);
    setUploadFile(null);
    setUploadPreviewUrl(null);
    setUploadPassword('');
    setUploadNote('');
  };

  const onUploadFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes((f.type || '').toLowerCase())) {
      toast.error(`Unsupported type: ${f.type || 'unknown'}. Use PNG, JPEG, or WEBP.`);
      e.target.value = '';
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error(`File too large: ${(f.size / 1024 / 1024).toFixed(1)} MB. Max 8 MB.`);
      e.target.value = '';
      return;
    }
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    setUploadFile(f);
    setUploadPreviewUrl(URL.createObjectURL(f));
    // v6.32.134 — aspect ratio warning. BLM template assumes ~4:3. Anything
    // outside 1.0:1 to 1.6:1 is flagged with a friendly warning toast.
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      if (ratio < 1.0 || ratio > 1.6) {
        toast.warning(
          `Aspect ratio ${ratio.toFixed(2)}:1 may render awkwardly in BLM PDFs (template assumes ~4:3 = 1.33:1). ` +
          `Consider cropping to 1024×768 or similar.`,
          { duration: 7000 },
        );
      }
    };
    img.src = URL.createObjectURL(f);
  };

  const submitUpload = async () => {
    if (!uploadTarget || !uploadFile) return;
    if (!uploadPassword) {
      toast.error('Password is required to commit the image.');
      return;
    }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('grade', uploadTarget.grade);
      fd.append('context', uploadTarget.context);
      fd.append('password', uploadPassword);
      if (uploadNote) fd.append('note', uploadNote);
      fd.append('image', uploadFile, uploadFile.name);
      const { headers } = authCfg();
      const { data } = await axios.post(
        `${API}/bp-auditor/manual-upload`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
      );
      if (data.ok) {
        // v6.32.134 — surface the new strict-verify + auto-regen-queue fields
        const verifyTxt = data.verify_gate_passed
          ? `✓ WIDA verify passed${data.verify_notes ? ` (${data.verify_notes})` : ''}.`
          : '⚠ verify status unknown.';
        const regenTxt = data.regen_queued
          ? 'Press-regen auto-queued — BLM PDFs will refresh on next regen run.'
          : (data.regen_skip_reason === 'regen_already_running'
              ? 'Regen already running; this scene is part of the active queue.'
              : 'Regen not queued — click Regen 112 BLMs above to refresh booklets.');
        toast.success(
          `Image committed for Gr ${uploadTarget.grade} · ${uploadTarget.context}. ` +
          `${data.question_count} question${data.question_count === 1 ? '' : 's'} updated. ` +
          `${data.manual_review_auto_removed ? 'Removed from Manual Review Queue. ' : ''}` +
          `${verifyTxt} ${regenTxt}`,
          { duration: 8000 },
        );
        closeUploadDialog();
        fetchManualReview();
      } else {
        toast.error(`Upload failed: ${data.error || 'unknown error'}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      const status = e?.response?.status;
      // v6.32.134 — 422 from strict-verify gate gets a richer toast
      if (status === 422 && String(detail).includes('strict-verify gate')) {
        toast.error(detail, { duration: 12000 });
      } else {
        toast.error(`Upload failed: ${detail}`);
      }
    } finally {
      setUploadBusy(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchRecentRuns();
    fetchManualReview();  // v6.32.131
    // Poll /status every 5s while a run is active.
    pollRef.current = setInterval(() => {
      setStatus((prev) => {
        const running = prev?.status === 'running' || prev?.status === 'starting';
        if (running) fetchStatus();
        return prev;
      });
    }, 5000);
    // v6.32.127 — poll /diagnostics for recent runs every 30s (slower, since
    // /diagnostics is more expensive). Also refresh whenever a run transitions
    // running → complete/failed so the history reflects the newest result.
    const runsInterval = setInterval(() => { fetchRecentRuns(); }, 30000);
    // v6.32.131 — refresh manual-review queue on the same cadence as recent runs
    const mrInterval = setInterval(() => { fetchManualReview(); }, 30000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      clearInterval(runsInterval);
      clearInterval(mrInterval);
    };
  }, [fetchStatus, fetchRecentRuns, fetchManualReview]);

  // v6.32.127 — refresh recent runs immediately whenever the active status
  // flips to a terminal state, so the newest run lands in the history.
  // v6.34.4 — also play the completion chime on that same transition.
  // Use a ref to track the prior status so the chime only fires on the
  // running→terminal edge (not on every render once status is terminal).
  const prevReverifyStatusRef = useRef(null);
  useEffect(() => {
    const s = status?.status;
    const prev = prevReverifyStatusRef.current;
    if (s === 'complete' || s === 'failed' || s === 'cancelled') {
      // Only chime + refetch on the transition into terminal.
      if (prev && !['complete', 'failed', 'cancelled'].includes(prev)) {
        if (s === 'complete') {
          playSuccessChime();
          // v6.35.7 — desktop notification mirrors the chime.
          notifyOnCompletion({
            title: 'BP Re-Verify complete',
            body: `${status?.processed || 0}/${status?.total || 0} scenes processed`,
            success: true,
          });
        } else {
          playFailureChime();
          notifyOnCompletion({
            title: `BP Re-Verify ${s.toUpperCase()}`,
            body: status?.failure_reason || status?.phase || 'see Recent Runs for details',
            success: false,
          });
        }
        fetchRecentRuns();
      }
    }
    prevReverifyStatusRef.current = s || null;
  }, [status?.status, status?.run_id, fetchRecentRuns]);

  const launchRun = async () => {
    if (grades.length === 0) {
      toast.error('Pick at least one grade band');
      return;
    }
    // v6.35.7 — request desktop notification permission on this user
    // gesture so the operator gets pinged when the Re-Verify finishes.
    ensureNotificationPermission();
    setLaunching(true);
    setTriggerOpen(false);
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/reverify-all/run`,
        { grades, attempts_cap: attemptsCap, force_gpt_image_1: forceGptImage1 },
        authCfg(),
      );
      if (data.already_running) {
        toast.info('A re-verify run is already in progress. Progress panel shows live status.');
      } else {
        toast.success(
          forceGptImage1
            ? `Re-verify launched (MOSAIC image engine only, skipping MOSAIC image engine) for ${grades.join(', ')}.`
            : `Re-verify launched for ${grades.join(', ')} (attempts=${attemptsCap}). ` +
              `Polling every 5s. Expect 10-20 min for a full run.`
        );
      }
      fetchStatus();
    } catch (e) {
      toast.error(`Launch failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLaunching(false);
    }
  };

  const triggerBlmRegen = async () => {
    if (!window.confirm(
      'Regenerate all 112 BLM booklets so they pick up the freshly-remediated images? (~3 min, auto-retry enabled)'
    )) return;
    try {
      await axios.post(
        `${API}/bp-auditor/reverify-all/trigger-blm-regen`, {}, authCfg(),
      );
      toast.success('BLM regen started (auto-retry enabled). Watch the Press-PDF status chip.');
    } catch (e) {
      toast.error(`BLM regen trigger failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // v6.32.126 — one-click retry for a single stubborn scene via GPT Image 1.
  const [retryingKey, setRetryingKey] = useState(null);

  // v6.36.45 — Manual Accept-Clean (SME override for stubborn LLM verdicts).
  // Canonical use case: Gemini strict-vision keeps flagging iconographic
  // marks (musical notes, currency symbols, math operators, arrows) as
  // "text violations" even though visual inspection confirms the image
  // is WIDA-clean. This dialog captures the operator's reason and the
  // literal "ACCEPT" confirmation required by the backend endpoint.
  const [acceptDialog, setAcceptDialog] = useState({
    open: false, grade: null, context: null, last_note: null,
  });
  const [acceptReason, setAcceptReason] = useState('');
  const [acceptConfirm, setAcceptConfirm] = useState('');
  const [acceptingKey, setAcceptingKey] = useState(null);

  const openAcceptDialog = (s) => {
    setAcceptDialog({
      open: true, grade: s.grade, context: s.context, last_note: s.last_note,
    });
    setAcceptReason('');
    setAcceptConfirm('');
  };
  const closeAcceptDialog = () => {
    setAcceptDialog({ open: false, grade: null, context: null, last_note: null });
    setAcceptReason('');
    setAcceptConfirm('');
  };
  const submitManualAccept = async () => {
    if ((acceptConfirm || '').trim() !== 'ACCEPT') {
      toast.error('Type ACCEPT exactly to confirm. Refusing override.');
      return;
    }
    if ((acceptReason || '').trim().length < 20) {
      toast.error('Reason must be at least 20 characters — the override must be defensibly documented.');
      return;
    }
    const key = `${acceptDialog.grade}|${acceptDialog.context}`;
    setAcceptingKey(key);
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/manual-accept-clean`,
        {
          grade: acceptDialog.grade,
          context: acceptDialog.context,
          reason: acceptReason.trim(),
          confirm_text: 'ACCEPT',
        },
        authCfg(),
      );
      toast.success(
        `Marked accepted: Gr ${data.grade} · ${data.context} (sha=${(data.sha256 || '').slice(0, 12)}…). ` +
        `Removed from ${data.removed_from_runs} stubborn run(s).`
      );
      closeAcceptDialog();
      fetchStatus();
    } catch (e) {
      toast.error(`Manual accept failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setAcceptingKey(null);
    }
  };

  // v6.36.46 — Reset Stubborn (clear false-positive flags from a stale
  // Re-Verify run). Use case: after deploying the v6.36.46 resilience
  // patch, the operator needs to clear the legacy bogus stubborn array
  // that was produced before retry-on-transient-error + cross-tier
  // image loading landed. Unlike Mark Accepted (per-scene SME override
  // with SHA stamp), this is a single bulk-clear that touches NO
  // customer-content collections — only the stubborn array on the
  // bp_reverify_runs row.
  const [resetDialog, setResetDialog] = useState({ open: false });
  const [resetReason, setResetReason] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  const openResetDialog = () => {
    setResetDialog({ open: true });
    setResetReason('');
    setResetConfirm('');
  };
  const closeResetDialog = () => {
    setResetDialog({ open: false });
    setResetReason('');
    setResetConfirm('');
  };
  const submitReset = async () => {
    if ((resetConfirm || '').trim() !== 'RESET') {
      toast.error('Type RESET exactly to confirm. Refusing to clear.');
      return;
    }
    if ((resetReason || '').trim().length < 20) {
      toast.error('Reason must be at least 20 characters — clearing must be defensibly documented.');
      return;
    }
    setResetting(true);
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/reverify-all/reset-stubborn`,
        { confirm_text: 'RESET', reason: resetReason.trim() },
        authCfg(),
      );
      toast.success(
        `Cleared ${data.cleared_count} stubborn flag(s) on run ${(data.run_id || '').slice(-18)}.`
      );
      closeResetDialog();
      fetchStatus();
    } catch (e) {
      toast.error(`Reset failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setResetting(false);
    }
  };

  // v6.32.130 — retryOneStubborn now WAITS for the single-scene worker
  // to finish (poll /status until status is no longer running/starting,
  // or a 6-min hard cap). Pre-v6.32.130 it returned after 30s of polling,
  // which meant the "Retry All Stubborn" sequential loop tried to launch
  // the next retry while the previous was still mid-LLM-call → got
  // rejected with already_running:true. Now the loop chains cleanly.
  const retryOneStubborn = async (s) => {
    const key = `${s.grade}|${s.context}`;
    if (retryingKey) return;
    setRetryingKey(key);
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/reverify-all/retry-one`,
        { grade: s.grade, context: s.context, force_gpt_image_1: true, attempts_cap: 1 },
        authCfg(),
      );
      if (data.already_running) {
        toast.info(`Cannot retry — a re-verify run is already in progress (${data.active_run_id || 'unknown'}).`);
        return;
      }
      if (!data.ok) {
        toast.error(`Retry failed: ${data.error || 'unknown error'}`);
        return;
      }
      toast.success(`Retry launched for Gr ${s.grade} · ${s.context} via MOSAIC image engine.`);
      fetchStatus();
      // v6.32.130 — poll every 5s for up to 6 min (~72 polls) waiting
      // for status to clear from running/starting → complete/failed.
      const MAX_POLLS = 72;
      for (let i = 0; i < MAX_POLLS; i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, 5000));
        // eslint-disable-next-line no-await-in-loop
        const { data: latest } = await axios.get(`${API}/bp-auditor/reverify-all/status`, authCfg());
        if (latest && latest.status !== 'running' && latest.status !== 'starting' && latest.status !== 'queued') {
          fetchStatus();
          return;
        }
      }
      // hard cap reached — let the loop continue anyway
      fetchStatus();
    } catch (e) {
      toast.error(`Retry failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRetryingKey(null);
    }
  };

  const toggleGrade = (g) => {
    setGrades((prev) => prev.includes(g)
      ? prev.filter((x) => x !== g)
      : [...prev, g]);
  };

  const running = status?.status === 'running' || status?.status === 'starting';
  const complete = status?.status === 'complete';
  const processed = status?.processed || 0;
  const total = status?.total || 0;
  const pct = total ? Math.round((processed / total) * 100) : 0;
  const stubborn = status?.stubborn || [];

  return (
    <div className="bg-white border border-purple-200 rounded-xl p-6 space-y-4" data-testid="bp-reverify-all-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={20} className="text-purple-600" />
            <h3 className="text-lg font-bold">Full-Scope BP Image Re-Verify</h3>
            <Badge className="bg-purple-100 text-purple-800 border-purple-300 border" variant="outline">
              strict MOSAIC engine-2.0-flash + MOSAIC image engine auto-retry
            </Badge>
            {status?.force_gpt_image_1 && (running || complete) && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border" variant="outline">
                MOSAIC image engine only
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-600 max-w-3xl">
            Re-checks every committed BP image (all 110 scenes across all grade bands) via
            strict MOSAIC engine vision to catch text that MOSAIC content engine Vision may have missed (subway
            A/C/F roundels, single-letter logos, uniform patches, storefront signs).
            Any flagged scene is auto-regenerated via <strong>MOSAIC image engine</strong>
            (up to {attemptsCap} attempts), then falls back to <strong>the MOSAIC image engine</strong>
            (tighter text-adherence) for any still-stubborn scene. Remaining stubborn scenes
            are surfaced below for manual follow-up. Zero downtime — runs in background.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            data-testid="bp-reverify-all-refresh"
          >
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          {complete && status?.remediated > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={triggerBlmRegen}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="bp-reverify-all-blm-regen"
            >
              <Zap size={14} className="mr-1" /> Regen 112 BLMs
            </Button>
          )}
          <Button
            onClick={() => setTriggerOpen(true)}
            disabled={running || launching}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="bp-reverify-all-launch"
          >
            {running
              ? <><Loader2 size={14} className="mr-1 animate-spin" /> Running…</>
              : launching
                ? <><Loader2 size={14} className="mr-1 animate-spin" /> Launching…</>
                : <><Play size={14} className="mr-1" /> Run Re-Verify</>}
          </Button>
        </div>
      </div>

      {/* Live progress while running */}
      {running && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-purple-900 font-semibold">
              {status?.current_scene || 'Starting…'}
            </span>
            <span className="text-purple-700 font-mono">{processed}/{total} ({pct}%)</span>
          </div>
          <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500 transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="grid grid-cols-3 gap-2 text-center pt-2">
            <Stat label="Already Clean" value={status?.already_clean || 0} color="emerald" />
            <Stat
              label="Remediated"
              value={status?.remediated || 0}
              color="indigo"
              sub={
                status?.remediated_by_model
                  ? `${status.remediated_by_model['nano-banana'] || 0} NB · ${status.remediated_by_model['mosaic-image-engine'] || 0} GPT-I1`
                  : null
              }
            />
            <Stat label="Stubborn" value={stubborn.length} color="rose" />
          </div>
        </div>
      )}

      {/* Complete-state summary */}
      {complete && (
        <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <span className="font-semibold">Run complete</span>
            <span className="text-gray-500 text-xs">
              started {(status?.started_at || '').slice(0, 19).replace('T', ' ')} UTC ·
              completed {(status?.completed_at || '').slice(0, 19).replace('T', ' ')} UTC
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <Stat label="Total scanned"  value={total} color="gray" />
            <Stat label="Already Clean"  value={status?.already_clean || 0} color="emerald" />
            <Stat
              label="Remediated"
              value={status?.remediated || 0}
              color="indigo"
              sub={
                status?.remediated_by_model
                  ? `${status.remediated_by_model['nano-banana'] || 0} NB · ${status.remediated_by_model['mosaic-image-engine'] || 0} GPT-I1`
                  : null
              }
            />
            <Stat label="Stubborn" value={stubborn.length} color="rose" />
          </div>

          {stubborn.length > 0 && (
            <div className="bg-white border border-rose-200 rounded p-3 text-xs space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <div className="font-semibold text-rose-800 flex items-center gap-1">
                  <AlertTriangle size={13} /> Scenes that couldn't reach zero-text after {status?.attempts_cap || 3} attempts:
                </div>
                {/* v6.36.46 — Bulk clear false-positive stubborn flags.
                    Use when a Re-Verify run on a stale codebase flagged
                    everything stubborn due to MOSAIC engine rate-limits / R2
                    cutover, not real text violations. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openResetDialog}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50 h-7 px-2 text-[11px] shrink-0"
                  data-testid="bp-reverify-reset-stubborn-btn"
                >
                  <RefreshCw size={11} className="mr-1" /> Clear false-positives
                </Button>
              </div>
              <div className="text-gray-600 text-[11px] mb-2">
                MOSAIC image engine insists on painting text for realism in these scenes.
                Click <strong>Retry via MOSAIC image engine</strong> next to a scene to one-click
                re-run it through MOSAIC image engine's tighter text-adherence model, or open the
                Text-Fix / Visual-Fix panels for a user-tuned prompt.
                <span className="block mt-1 text-amber-700">
                  <strong>If most rows say &ldquo;content-router.RateLimitError&rdquo; or &ldquo;file missing&rdquo;:</strong> the
                  Re-Verify hit transient infra (MOSAIC engine quota / pod-disk cache miss), not real text. Use
                  <strong>&nbsp;Clear false-positives</strong>&nbsp;and re-run when MOSAIC engine quota recovers.
                </span>
              </div>
              {stubborn.map((s, i) => {
                const key = `${s.grade}|${s.context}`;
                const isRetrying = retryingKey === key;
                const anyRetrying = retryingKey !== null;
                return (
                <div key={i} className="flex items-start gap-2 py-1 border-t first:border-t-0 border-rose-100" data-testid={`bp-reverify-stubborn-row-${i}`}>
                  <Badge variant="outline" className="bg-white text-[10px]">Gr {s.grade}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{s.context}</div>
                    {s.last_note && (
                      <div className="text-[10px] text-gray-500 truncate">last note: {s.last_note}</div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[10px] h-6 px-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    onClick={() => retryOneStubborn(s)}
                    disabled={anyRetrying || running}
                    data-testid={`bp-reverify-retry-one-${i}`}
                    title="Retry this scene through the MOSAIC image engine (tighter text-adherence). Disabled while a re-verify run is in progress."
                  >
                    {isRetrying ? (
                      <><Loader2 size={10} className="mr-1 animate-spin" /> Retrying…</>
                    ) : (
                      <><Zap size={10} className="mr-1" /> Retry via GPT-1</>
                    )}
                  </Button>
                  {/* v6.36.45 — Mark Accepted (Manual Review). For cases where the
                      automated stubborn flag is wrong (e.g. MOSAIC engine flagging
                      musical notes / iconographic glyphs as "text"). */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[10px] h-6 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => openAcceptDialog(s)}
                    disabled={acceptingKey === `${s.grade}|${s.context}` || running}
                    data-testid={`bp-reverify-manual-accept-${i}`}
                    title="Mark this scene as WIDA-clean by SME visual inspection. Use when the content-engine verifier is wrong (e.g. flagging musical notes or iconographic marks as text). Requires a 20+ character defensible reason."
                  >
                    {acceptingKey === `${s.grade}|${s.context}` ? (
                      <><Loader2 size={10} className="mr-1 animate-spin" /> Accepting…</>
                    ) : (
                      <><ShieldCheck size={10} className="mr-1" /> Mark Accepted</>
                    )}
                  </Button>
                </div>
                );
              })}
            </div>
          )}

          {complete && status?.remediated > 0 && (
            <div className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2">
              <FileCheck2 size={11} className="inline mr-1" />
              {status.remediated} image{status.remediated === 1 ? '' : 's'} were regenerated.
              Click <strong>Regen 112 BLMs</strong> above so the booklet PDFs pick up the updates.
            </div>
          )}
        </div>
      )}

      {/* v6.32.127 — Recent Runs history dashboard */}
      {/* v6.32.131 — Manual Review Queue (scenes structurally impossible for current image-gen models). */}
      {manualReview.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200" data-testid="bp-reverify-manual-review-queue">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-amber-700">
              Manual Review Queue <span className="text-gray-400 font-normal">· {manualReview.length} scene{manualReview.length === 1 ? '' : 's'}</span>
            </div>
            <div className="text-[10px] text-gray-500">Skipped by full sweeps + retry-one until un-flagged</div>
          </div>
          <ul className="text-[11px] space-y-1">
            {manualReview.map((m, i) => (
              <li key={`${m.grade}|${m.context}`} className="flex items-start gap-2 py-0.5 border-t first:border-t-0 border-amber-100 bg-amber-50/40 px-2 rounded" data-testid={`bp-reverify-manual-review-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-[10px] font-semibold">Gr {m.grade} · {m.context}</span>
                  {m.reason && <span className="text-gray-600"> — {m.reason}</span>}
                  <div className="text-[9px] text-gray-400">
                    flagged by {m.flagged_by} · {m.flagged_at ? new Date(m.flagged_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openUploadDialog(m)}
                  disabled={manualReviewBusy || uploadBusy}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed rounded whitespace-nowrap"
                  data-testid={`bp-reverify-manual-review-upload-${i}`}
                  title="Upload a text-free, culturally-faithful image to commit directly to this scene. PNG/JPEG/WEBP, max 8 MB."
                >
                  <Upload size={10} /> Upload Image
                </button>
                <button
                  type="button"
                  onClick={() => unflagManualReview(m)}
                  disabled={manualReviewBusy}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 text-[10px] font-semibold disabled:opacity-40 rounded whitespace-nowrap"
                  data-testid={`bp-reverify-manual-review-unflag-${i}`}
                  title="Un-flag this scene. It becomes eligible for retry-one and full sweeps again."
                >
                  Un-flag
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <RecentRunsHistory
        recentRuns={recentRuns}
        retryOneStubborn={retryOneStubborn}
        retryingKey={retryingKey}
        running={running}
        flagManualReview={flagManualReview}
        manualReviewBusy={manualReviewBusy}
      />

      {!running && !complete && (
        <div className="text-xs text-gray-500 italic">
          No re-verify run yet. Click <strong>Run Re-Verify</strong> to scan all committed BP images.
        </div>
      )}

      {/* Launch-configuration dialog */}
      <Dialog open={triggerOpen} onOpenChange={(v) => !v && setTriggerOpen(false)}>
        <DialogContent data-testid="bp-reverify-all-config-dialog">
          <DialogHeader>
            <DialogTitle>Launch Full-Scope Re-Verify</DialogTitle>
            <DialogDescription>
              Scans every committed BP image in the selected grade bands. Re-generates
              any image whose strict vision audit flags text/logos. Runs in background —
              this page will show live progress.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-2">
                Grade bands to scan ({grades.length} selected)
              </label>
              <div className="flex flex-wrap gap-2">
                {['K', '1', '2', '3-4', '5-6', '7-8', '9-12'].map((g) => (
                  <Button
                    key={g}
                    variant={grades.includes(g) ? 'default' : 'outline'}
                    size="sm"
                    className={grades.includes(g) ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    onClick={() => toggleGrade(g)}
                    data-testid={`bp-reverify-grade-${g}`}
                  >
                    {g}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">
                Max regen attempts per flagged scene
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 5].map((n) => (
                  <Button
                    key={n}
                    variant={attemptsCap === n ? 'default' : 'outline'}
                    size="sm"
                    className={attemptsCap === n ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    onClick={() => setAttemptsCap(n)}
                  >
                    {n}
                  </Button>
                ))}
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Higher = more content-engine calls but better success on stubborn scenes. 3 is usually fine.
              </div>
            </div>
            <div className="bg-white border border-emerald-200 rounded p-3 space-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={forceGptImage1}
                  onChange={(e) => setForceGptImage1(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="bp-reverify-force-gpt-image-1"
                />
                <span className="text-xs font-semibold text-emerald-800">
                  Force MOSAIC image engine (skip MOSAIC image engine entirely)
                </span>
              </label>
              <div className="text-[11px] text-gray-600 ml-6">
                Pro mode for stubborn-scene sweeps. Skips the fast MOSAIC image engine stage
                and goes straight to MOSAIC image engine (higher cost but tighter text-adherence).
                Useful for grade bands you already know are text-heavy
                (Chinatown in 3-4, subway in 5-6).
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900">
              Estimated run time: ~{Math.round((grades.length * 16 * (forceGptImage1 ? 8 : 6)) / 60)}-{Math.round((grades.length * 16 * (forceGptImage1 ? 12 : 10)) / 60)} min
              (scales with grades × scenes{forceGptImage1 ? '' : ' × attempts'}). All generation happens in background.
            </div>
            <ProductionImpactDisclosure
              testId="bp-reverify-all-disclosure"
              removes={[
                <>Every committed BP image in selected grade bands is re-audited via strict MOSAIC engine vision</>,
                <>Any image flagged as containing text/logos is regenerated via {forceGptImage1 ? <>MOSAIC image engine directly</> : <>MOSAIC image engine (up to {attemptsCap} attempts), with MOSAIC image engine as fallback</>}</>,
                <>Successful regenerations replace files in <code className="bg-gray-50 px-1">/bp_images/</code> + update <code className="bg-gray-50 px-1">questions.bp_image_url</code> in MongoDB</>,
                <>Each replacement is logged to <code className="bg-gray-50 px-1">bp_audit_manifests</code> (rollback-eligible) + <code className="bg-gray-50 px-1">audit_logs</code></>,
                <>If "Auto-regen BLMs" is checked, the 113 BLM print booklets regenerate at the end (~3-5 min)</>,
              ]}
              doesNotTouch={[
                <>Question text, answers, rubrics, scoring logic — only image bytes/URLs change</>,
                <>Student responses, in-progress exams, sessions, assessment results</>,
                <>Marketing collateral PDFs, sell sheet, procurement letters</>,
                <>License data, school configs, user accounts</>,
                <>Stubborn scenes that fail all attempts are listed but not modified — they stay as-is</>,
              ]}
              note={`content-engine cost: ~$${forceGptImage1 ? '5-15' : '1-5'} per full sweep depending on grade selection. All work happens in background — you can close this tab and return later.`}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTriggerOpen(false)} disabled={launching}>
              Cancel
            </Button>
            <Button
              onClick={launchRun}
              disabled={launching || grades.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="bp-reverify-all-confirm-launch"
            >
              {launching
                ? <><Loader2 size={14} className="mr-1 animate-spin" /> Launching…</>
                : <><Play size={14} className="mr-1" /> Launch Run</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v6.32.132 — Manual Image Upload dialog (per-scene upload from Manual Review Queue) */}
      <Dialog open={!!uploadTarget} onOpenChange={(open) => { if (!open && !uploadBusy) closeUploadDialog(); }}>
        <DialogContent className="max-w-lg" data-testid="bp-reverify-manual-upload-dialog">
          <DialogHeader>
            <DialogTitle>Manual Image Upload</DialogTitle>
            <DialogDescription className="text-xs">
              {uploadTarget && (
                <>Commit your own image for <strong>Gr {uploadTarget.grade} · {uploadTarget.context}</strong>. PNG / JPEG / WEBP, max 8&nbsp;MB, 256×256 to 4096×4096.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
              <strong>Atomic commit:</strong> writes a rollback manifest, updates every linked question's image URL, mirrors to graphic_support_url, and auto-removes this scene from the Manual Review Queue. Rollback is always available via the Content Audit Manifests view.
            </div>
            <div className="rounded border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-900">
              <strong>Strict WIDA verify:</strong> the image is checked by MOSAIC engine-2.0-flash for ANY readable text (signage, watermarks, logos, photographer credits). If text is detected, the commit is REJECTED with the model's notes. Press-regen is auto-queued on success.
            </div>

            <label className="block text-xs font-semibold text-gray-700">
              Image file
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onUploadFileChange}
                disabled={uploadBusy}
                className="mt-1 block w-full text-xs file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-emerald-600 file:text-white file:font-semibold file:cursor-pointer disabled:opacity-40"
                data-testid="bp-reverify-manual-upload-file"
              />
            </label>

            {uploadPreviewUrl && (
              <div className="border border-gray-200 rounded p-2 bg-gray-50">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Preview</div>
                <img
                  src={uploadPreviewUrl}
                  alt="upload preview"
                  className="max-h-56 max-w-full mx-auto"
                  data-testid="bp-reverify-manual-upload-preview"
                />
                {uploadFile && (
                  <div className="text-[10px] text-gray-500 mt-1 text-center">
                    {uploadFile.name} · {(uploadFile.size / 1024).toFixed(1)} KB · {uploadFile.type}
                  </div>
                )}
              </div>
            )}

            <label className="block text-xs font-semibold text-gray-700">
              Password (re-auth)
              <input
                type="password"
                value={uploadPassword}
                onChange={(e) => setUploadPassword(e.target.value)}
                disabled={uploadBusy}
                placeholder="Your MOSAIC password"
                className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-40"
                data-testid="bp-reverify-manual-upload-password"
              />
            </label>

            <label className="block text-xs font-semibold text-gray-700">
              Note (optional)
              <input
                type="text"
                value={uploadNote}
                onChange={(e) => setUploadNote(e.target.value)}
                disabled={uploadBusy}
                placeholder="e.g. Unsplash royalty-free, photographer credit, designer commission ref"
                maxLength={500}
                className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-40"
                data-testid="bp-reverify-manual-upload-note"
              />
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={closeUploadDialog}
              disabled={uploadBusy}
              data-testid="bp-reverify-manual-upload-cancel"
            >
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={submitUpload}
              disabled={uploadBusy || !uploadFile || !uploadPassword}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bp-reverify-manual-upload-submit"
            >
              {uploadBusy
                ? <><Loader2 size={14} className="mr-1 animate-spin" /> Committing…</>
                : <><Upload size={14} className="mr-1" /> Commit Image</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v6.36.45 — Mark Accepted (Manual Review) dialog */}
      <Dialog open={acceptDialog.open} onOpenChange={(v) => !v && closeAcceptDialog()}>
        <DialogContent className="max-w-lg" data-testid="bp-reverify-manual-accept-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-emerald-600" />
              Mark Accepted (Manual Review)
            </DialogTitle>
            <DialogDescription>
              Override the automated stubborn flag for this scene. Use when SME
              visual inspection confirms the image is WIDA-clean (e.g. the
              content-engine verifier is flagging iconographic marks like musical notes,
              currency symbols, math operators, or arrows as "text").
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="rounded border border-gray-200 bg-gray-50 p-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Scene</div>
              <div>
                <Badge variant="outline" className="bg-white text-[10px] mr-1">
                  Gr {acceptDialog.grade}
                </Badge>
                <span className="font-medium">{acceptDialog.context}</span>
              </div>
              {acceptDialog.last_note && (
                <div className="text-[10px] text-gray-500 mt-1">
                  prior content-engine note: <span className="italic">{acceptDialog.last_note}</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                SME reason (≥ 20 characters, permanently archived in audit log)
              </label>
              <textarea
                value={acceptReason}
                onChange={(e) => setAcceptReason(e.target.value)}
                placeholder="e.g. Musical notes are iconographic auditory-cue scaffolding for the WIDA Listening item 'How do you know the ice cream truck is coming? → By its song.' Notes are NOT orthographic text; image is WIDA-compliant."
                rows={5}
                className="w-full border rounded p-2 text-[11px] font-mono"
                data-testid="bp-reverify-manual-accept-reason"
              />
              <div className={`text-[10px] mt-1 ${acceptReason.trim().length >= 20 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {acceptReason.trim().length} / 20+ characters
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Type <code className="bg-amber-100 px-1 rounded">ACCEPT</code> to confirm
              </label>
              <input
                type="text"
                value={acceptConfirm}
                onChange={(e) => setAcceptConfirm(e.target.value)}
                placeholder="ACCEPT"
                className="w-full border rounded p-2 text-[11px] font-mono uppercase tracking-widest"
                data-testid="bp-reverify-manual-accept-confirm"
              />
            </div>
            <div className="text-[10px] text-gray-500 leading-relaxed border-t pt-2">
              <strong>What this does:</strong> writes a SHA256 stamp to <code>bp_image_revisions</code> with
              <code> source_model="manual-accept-clean"</code>, removes the scene from the stubborn list, and writes
              one consolidated <code>audit_logs</code> row with your operator email + reason text. Touches NOTHING in
              questions / assessments / answer keys / TEs / BLMs / customer-content. The image bytes on disk are NOT
              modified. The override is permanent by design (reversible only via manual DB edit).
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={closeAcceptDialog}
              disabled={!!acceptingKey}
              data-testid="bp-reverify-manual-accept-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={submitManualAccept}
              disabled={
                !!acceptingKey ||
                acceptReason.trim().length < 20 ||
                acceptConfirm.trim() !== 'ACCEPT'
              }
              data-testid="bp-reverify-manual-accept-submit"
            >
              {acceptingKey ? (
                <><Loader2 size={12} className="mr-1 animate-spin" /> Submitting…</>
              ) : (
                <><ShieldCheck size={12} className="mr-1" /> Confirm & Override</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v6.36.46 — Reset Stubborn (bulk clear false-positive flags) dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(v) => !v && closeResetDialog()}>
        <DialogContent className="max-w-lg" data-testid="bp-reverify-reset-stubborn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw size={16} className="text-amber-600" />
              Clear false-positive stubborn flags
            </DialogTitle>
            <DialogDescription>
              Bulk-clear the stubborn array on the most recent Re-Verify run.
              Use when a run hit transient infra failures (MOSAIC engine rate-limit,
              missing local image files) and marked many scenes stubborn that
              are NOT actually text-violating.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
              This will reset <strong>{stubborn.length}</strong> stubborn flag(s) to
              empty on the latest run. The bp_reverify_runs row is updated
              atomically with a `stubborn_reset_reason` audit-trail field; one
              consolidated audit_logs row is written. <strong>NO</strong> image
              bytes are modified. <strong>NO</strong> SHA stamps are written.
              <strong>NO</strong> customer-content collections are touched.
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Reason (≥ 20 characters, permanently archived in audit log)
              </label>
              <textarea
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                placeholder="e.g. Re-Verify run on 2026-06-04 hit MOSAIC engine RateLimitError and produced 110 false-positive stubborn flags. v6.36.46 retry-on-transient patch deployed; clearing legacy bogus array so the next clean run can re-evaluate."
                rows={5}
                className="w-full border rounded p-2 text-[11px] font-mono"
                data-testid="bp-reverify-reset-reason"
              />
              <div className={`text-[10px] mt-1 ${resetReason.trim().length >= 20 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {resetReason.trim().length} / 20+ characters
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Type <code className="bg-amber-100 px-1 rounded">RESET</code> to confirm
              </label>
              <input
                type="text"
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="RESET"
                className="w-full border rounded p-2 text-[11px] font-mono uppercase tracking-widest"
                data-testid="bp-reverify-reset-confirm"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={closeResetDialog}
              disabled={resetting}
              data-testid="bp-reverify-reset-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={submitReset}
              disabled={
                resetting ||
                resetReason.trim().length < 20 ||
                resetConfirm.trim() !== 'RESET'
              }
              data-testid="bp-reverify-reset-submit"
            >
              {resetting ? (
                <><Loader2 size={12} className="mr-1 animate-spin" /> Clearing…</>
              ) : (
                <><RefreshCw size={12} className="mr-1" /> Confirm Clear</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, color, sub }) => {
  const colorMap = {
    gray: 'text-gray-900',
    emerald: 'text-emerald-700',
    indigo: 'text-indigo-700',
    rose: 'text-rose-700',
  };
  return (
    <div className="bg-white border rounded p-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${colorMap[color] || 'text-gray-900'}`}>{value}</div>
      {sub && <div className="text-[9px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
};

// v6.32.127 — Recent Runs history dashboard.
// Reads recent_mongo_runs[] from the v6.32.119 /diagnostics endpoint
// (which surfaces the v6.32.121 bp_reverify_runs Mongo collection).
// Each row is expandable to show the full traceback (if any) so the
// operator never needs to curl /status or open Mongo Compass to debug.
const RecentRunsHistory = ({ recentRuns, retryOneStubborn, retryingKey, running, flagManualReview, manualReviewBusy }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [retryAllQueueIdx, setRetryAllQueueIdx] = useState(null);  // v6.32.130 — queue position when "Retry All" running
  if (!recentRuns || recentRuns.length === 0) return null;

  // v6.32.130 — "Retry All Stubborn" — sequential one-click sweep.
  // Iterates the stubborn list and calls retryOneStubborn for each scene,
  // waiting for the previous retry to clear (retryingKey === null) before
  // launching the next. Each retry is a 1-scene worker (~3 min) that
  // CANNOT trip the 15-min watchdog. UI shows queue position.
  const retryAllStubborn = async (stubbornList) => {
    if (!stubbornList || !stubbornList.length) return;
    if (retryingKey || running) return;
    for (let n = 0; n < stubbornList.length; n++) {
      setRetryAllQueueIdx(n + 1);
      // eslint-disable-next-line no-await-in-loop
      await retryOneStubborn(stubbornList[n]);
      // Each retry-one returns after kicking off; we need to wait until
      // the backend run finishes before launching the next. Poll the
      // parent's retryingKey state — when it returns to null AND the
      // active /status has cleared, advance.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        const t = setInterval(() => {
          // retryOneStubborn already clears retryingKey in its finally.
          // We also gate on the backend run completing — a fresh retry
          // would be rejected with already_running:true. Poll every 5s.
          resolve();
          clearInterval(t);
        }, 6000);  // 6s grace between sequential retries
      });
    }
    setRetryAllQueueIdx(null);
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso.slice(0, 16); }
  };
  const fmtDuration = (startIso, endIso) => {
    if (!startIso) return '—';
    const end = endIso ? new Date(endIso) : new Date();
    const start = new Date(startIso);
    const sec = Math.max(0, Math.floor((end - start) / 1000));
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  };
  const statusBadge = (s) => {
    switch (s) {
      case 'complete': return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">complete</Badge>;
      case 'failed': return <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">failed</Badge>;
      case 'running': return <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]">running</Badge>;
      case 'starting': return <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">starting</Badge>;
      case 'queued': return <Badge className="bg-gray-100 text-gray-700 border-gray-200 text-[10px]">queued</Badge>;
      default: return <Badge variant="outline" className="text-[10px]">{s || '—'}</Badge>;
    }
  };
  const phaseHint = (r) => {
    if (r.phase === 'watchdog_stale') return '⏱ killed by watchdog (no heartbeat 15+ min)';
    if (r.phase === 'cancelled_by_admin') return '🛑 cancelled by admin';
    if (r.phase === 'thread_runner_crashed') return '💥 thread runner crashed';
    if (r.phase === 'retry_runner_crashed') return '💥 retry runner crashed';
    return r.phase || '';
  };

  return (
    <div className="border-t border-gray-200 pt-3 mt-3" data-testid="bp-reverify-recent-runs-history">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-gray-700">
          Recent Runs <span className="text-gray-400 font-normal">· last {recentRuns.length}</span>
        </div>
        <div className="text-[10px] text-gray-400">auto-refresh every 30s</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-1 pr-2 font-medium">Run</th>
              <th className="py-1 pr-2 font-medium">Status</th>
              <th className="py-1 pr-2 font-medium">Progress</th>
              <th className="py-1 pr-2 font-medium">Started</th>
              <th className="py-1 pr-2 font-medium">Duration</th>
              <th className="py-1 pr-2 font-medium">Kind</th>
              <th className="py-1 pr-2 font-medium">By</th>
              <th className="py-1 font-medium" />
            </tr>
          </thead>
          <tbody>
            {recentRuns.map((r, i) => {
              const isExpanded = expandedIdx === i;
              const hasDetails = (r.failure_reason || r.traceback || (r.stubborn && r.stubborn.length));
              const total = (r.target_scene_keys && r.target_scene_keys.length)
                || (r.processed != null && (r.already_clean + r.remediated + (r.stubborn?.length || 0)) > 0
                    ? r.processed + ((r.target_grades || []).length === 0 ? 0 : 0)
                    : 110);
              const endIso = r.completed_at || r.failed_at;
              const procDisplay = r.kind === 'retry_one'
                ? `${r.processed ?? 0}/1`
                : `${r.processed ?? 0}/${r.target_scene_keys?.length ? r.target_scene_keys.length : 110}`;
              return (
                <React.Fragment key={r.run_id || i}>
                  <tr
                    className={`border-b border-gray-100 ${hasDetails ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                    onClick={() => hasDetails && setExpandedIdx(isExpanded ? null : i)}
                    data-testid={`bp-reverify-recent-row-${i}`}
                  >
                    <td className="py-1 pr-2 font-mono text-[10px] text-gray-600">
                      {(r.run_id || '').replace(/^(reverify|retry)_/, '').slice(0, 19) || '—'}
                    </td>
                    <td className="py-1 pr-2">{statusBadge(r.status)}</td>
                    <td className="py-1 pr-2">
                      <span className="font-mono">{procDisplay}</span>
                      {(r.already_clean > 0 || r.remediated > 0 || r.stubborn?.length > 0) && (
                        <span className="text-gray-500 ml-1">
                          ({r.already_clean || 0}✓ {r.remediated || 0}↻ {r.stubborn?.length || 0}⚠)
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-2 text-gray-600">{fmtTime(r.started_at)}</td>
                    <td className="py-1 pr-2 text-gray-600">{fmtDuration(r.started_at, endIso)}</td>
                    <td className="py-1 pr-2">
                      {r.kind === 'retry_one' ? (
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">retry-one</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">full</Badge>
                      )}
                    </td>
                    <td className="py-1 pr-2 text-gray-600 truncate max-w-[120px]" title={r.by || ''}>{r.by || '—'}</td>
                    <td className="py-1 text-right text-gray-400">
                      {hasDetails ? (isExpanded ? '▾' : '▸') : ''}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-gray-50 border-b border-gray-100" data-testid={`bp-reverify-recent-row-${i}-details`}>
                      <td colSpan={8} className="py-2 px-2">
                        {phaseHint(r) && (
                          <div className="text-[11px] text-gray-700 mb-1">
                            <strong>phase:</strong> <span className="font-mono">{r.phase || '—'}</span>
                            {' '}<span className="text-gray-500">{phaseHint(r)}</span>
                          </div>
                        )}
                        {r.failure_reason && (
                          <div className="text-[11px] mb-1">
                            <strong>failure_reason:</strong> <span className="font-mono text-rose-700">{r.failure_reason}</span>
                          </div>
                        )}
                        {r.target_grades && r.target_grades.length > 0 && (
                          <div className="text-[11px] mb-1 text-gray-700">
                            <strong>target_grades:</strong> {r.target_grades.join(', ')}
                            {r.target_scene_keys && r.target_scene_keys.length > 0 && (
                              <span> · <strong>scene:</strong> <span className="font-mono">{r.target_scene_keys[0]}</span></span>
                            )}
                          </div>
                        )}
                        {r.stubborn && r.stubborn.length > 0 && (
                          <details className="text-[11px] mt-1" open>
                            <summary className="cursor-pointer text-gray-700 hover:text-gray-900 flex items-center justify-between flex-wrap gap-2">
                              <span>Stubborn scenes ({r.stubborn.length})</span>
                              {retryOneStubborn && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (retryingKey || running) return;
                                    // eslint-disable-next-line no-alert
                                    if (window.confirm(
                                      `Retry all ${r.stubborn.length} stubborn scenes via MOSAIC image engine, one at a time?\n\n` +
                                      `Each retry takes ~3 min. Total: ~${r.stubborn.length * 3} min.\n` +
                                      `You can leave this tab open; progress will update live.`
                                    )) retryAllStubborn(r.stubborn);
                                  }}
                                  disabled={!!retryingKey || running || !!retryAllQueueIdx}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-600 text-white text-[10px] font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                                  data-testid={`bp-reverify-recent-row-${i}-retry-all-stubborn`}
                                  title="Sequentially retry every stubborn scene via MOSAIC image engine. Each is a 1-scene worker that cannot hit the watchdog."
                                >
                                  {retryAllQueueIdx ? (
                                    <><Loader2 size={10} className="animate-spin" /> Retrying {retryAllQueueIdx}/{r.stubborn.length}…</>
                                  ) : (
                                    <><Zap size={10} /> Retry All Stubborn ({r.stubborn.length})</>
                                  )}
                                </button>
                              )}
                            </summary>
                            <ul className="ml-1 mt-2 text-gray-600 space-y-1">
                              {r.stubborn.slice(0, 20).map((s, k) => {
                                const key = `${s.grade}|${s.context}`;
                                const isRetrying = retryingKey === key;
                                const anyRetrying = !!retryingKey;
                                return (
                                  <li key={k} className="flex items-start gap-2 py-0.5 border-t first:border-t-0 border-gray-200">
                                    <div className="flex-1 min-w-0">
                                      <span className="font-mono text-[10px]">Gr {s.grade} · {s.context}</span>
                                      {s.last_note && (
                                        <span className="text-gray-500"> — {s.last_note.slice(0, 140)}</span>
                                      )}
                                    </div>
                                    {retryOneStubborn && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          retryOneStubborn(s);
                                        }}
                                        disabled={anyRetrying || running || !!retryAllQueueIdx}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-[10px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed rounded whitespace-nowrap"
                                        data-testid={`bp-reverify-recent-row-${i}-retry-${k}`}
                                        title="Retry just this scene via the MOSAIC image engine (tighter text-adherence). Single-scene worker — cannot hit the watchdog."
                                      >
                                        {isRetrying ? (
                                          <><Loader2 size={10} className="animate-spin" /> Retrying…</>
                                        ) : (
                                          <><Zap size={10} /> Retry via GPT-1</>
                                        )}
                                      </button>
                                    )}
                                    {flagManualReview && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          flagManualReview(s);
                                        }}
                                        disabled={anyRetrying || running || manualReviewBusy || !!retryAllQueueIdx}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed rounded whitespace-nowrap"
                                        data-testid={`bp-reverify-recent-row-${i}-flag-manual-${k}`}
                                        title="Mark this scene as 'Manual Review Pending' — it will be skipped by all future retries and full sweeps until you un-flag it. Use for scenes that are structurally impossible for current image-gen models (Chinatown signage, open book pages, maps)."
                                      >
                                        Mark Manual Review
                                      </button>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </details>
                        )}
                        {r.traceback && (
                          <details className="text-[11px] mt-1">
                            <summary className="cursor-pointer text-gray-700 hover:text-gray-900">
                              Traceback ({r.traceback.length} chars)
                            </summary>
                            <pre className="ml-2 mt-1 p-2 bg-white border border-gray-200 rounded font-mono text-[10px] text-gray-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                              {r.traceback}
                            </pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BPReverifyAllPanel;
